import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { policesAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme, pageTitleStyle, pageTitleTextStyle } from '../../theme';

const ORANGE = theme.orange;
const SS = {
  demande_ouverture: { bg:'#FEF3E2', color:'#9A5A00', dot:'#F18A00' },
  demande_refusee:   { bg:'#FCEBEB', color:'#A32D2D', dot:'#E24B4A' },
  demande_pieces:    { bg:'#FEF3E2', color:'#9A5A00', dot:'#F18A00' },
  en_cours_etude:    { bg:'#EDE9FE', color:'#5B21B6', dot:'#8B5CF6' },
  devis:      { bg:'#E6F1FB', color:'#185FA5', dot:'#378ADD' },
  souscrit:   { bg:'#E1F5EE', color:'#0F6E56', dot:'#1D9E75' },
  suspendu:   { bg:'#FCEBEB', color:'#A32D2D', dot:'#E24B4A' },
  resilie:    { bg:'#F1F1EE', color:'#5F5E5A', dot:'#888780' },
  sans_objet: { bg:'#F1F1EE', color:'#94A3B8', dot:'#CBD5E1' },
};

const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);
const fmt     = n => n ? new Intl.NumberFormat('fr-FR',{minimumFractionDigits:2}).format(n)+' €' : '—';
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const expiring = d => { if(!d) return false; const diff=(new Date(d)-new Date())/86400000; return diff<30&&diff>=0; };

export default function PortefeuilleContrats({ produit = 'rc_pro' }) {
  const navigate = useNavigate();
  const { t }    = useLanguage();
  const [items, setItems]   = useState([]);
  const [search, setSearch] = useState('');
  const [statut, setStatut] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchFocus, setSearchFocus] = useState(false);

  const isGF = produit === 'gf';

  const STATUTS = {
    demande_ouverture: t('status.demande_ouverture'), demande_refusee: t('status.demande_refusee'),
    demande_pieces: t('status.demande_pieces'), en_cours_etude: t('status.en_cours_etude'),
    devis: t('status.devis'), souscrit: t('status.souscrit'), suspendu: t('status.suspendu'),
    resilie: t('status.resilie'), sans_objet: t('status.sans_objet'),
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await policesAPI.list({ search, statut });
      const filtered = all.filter(p => isGF ? p.produit === 'gf' : (!p.produit || p.produit === 'rc_pro'));
      setItems(filtered);
    } finally { setLoading(false); }
  }, [search, statut, produit]);

  useEffect(() => { load(); }, [load]);

  const title = isGF ? t('gf.portfolio_title') : t('polices.portfolio_title');

  return (
    <div>
      {/* En-tête : titre souligné (style "Ligne") */}
      <div style={{ ...pageTitleStyle, marginBottom:16 }}>
        <span style={pageTitleTextStyle}>
          {isGF
            ? <SvgIcon size={22} color={ORANGE}><path d="M3 21l18 0M3 10l18 0M5 6l7 -3l7 3M4 10l0 11M20 10l0 11M8 14l0 3M12 14l0 3M16 14l0 3"/></SvgIcon>
            : <SvgIcon size={22} color={ORANGE}><path d="M11.46 20.846a12 12 0 0 1 -7.96 -14.846a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3a12 12 0 0 1 -.09 7.06M15 19l2 2l4 -4"/></SvgIcon>}
          {title}
        </span>
        <span style={{ fontSize:12, color:theme.textTertiary }}>
          {items.length} {t(items.length===1?'client_detail.contract_count':'client_detail.contract_count_plural')}
        </span>
        <div style={{ marginLeft:'auto', fontSize:12, color:theme.textTertiary, display:'flex', alignItems:'center', gap:7 }}>
          <SvgIcon size={15} color={theme.textTertiary}><path d="M12 9h.01M11 12h1v4h1M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0 -18"/></SvgIcon>
          {t('polices.create_from_client')}
        </div>
      </div>

      {/* Recherche + filtre statut */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:220, display:'flex', alignItems:'center', gap:8,
          border:`1px solid ${searchFocus ? ORANGE : '#D9D9D5'}`, borderRadius:8, padding:'0 12px', background:'#fff',
          boxShadow: searchFocus ? '0 0 0 3px rgba(241,138,0,0.15)' : 'none', transition:'all .12s' }}>
          <SvgIcon size={16} color="#A0A0A0"><path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0M21 21l-6 -6"/></SvgIcon>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            onFocus={()=>setSearchFocus(true)} onBlur={()=>setSearchFocus(false)}
            placeholder={t('polices.col_client') + '…'}
            style={{ flex:1, border:'none', outline:'none', padding:'10px 0', fontSize:14, fontFamily:'inherit', background:'transparent' }}/>
        </div>
        <select value={statut} onChange={e=>setStatut(e.target.value)}
          style={{ border:'1px solid #D9D9D5', borderRadius:8, padding:'10px 12px', fontSize:14, minWidth:160, fontFamily:'inherit', cursor:'pointer' }}>
          <option value="">{t('common.all_statuses')}</option>
          {Object.keys(SS).map(v=><option key={v} value={v}>{STATUTS[v]}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ color:'#64748B', padding:24 }}>{t('common.loading')}</div>
      ) : (
        <div style={{ background:'#fff', borderRadius:12, border:'0.5px solid #E8E8E5', overflow:'hidden' }}>
          {items.map((item, idx) => {
            const s   = SS[item.statut]||SS.devis;
            const exp = expiring(item.date_echeance);
            const route = isGF ? `/gf/${item.id}` : `/polices/${item.id}`;
            return (
              <div key={item.id} onClick={()=>navigate(route)}
                style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', cursor:'pointer',
                  borderBottom: idx < items.length - 1 ? '0.5px solid #F1F1EE' : 'none' }}
                onMouseEnter={e=>e.currentTarget.style.background='#FAFAF8'}
                onMouseLeave={e=>e.currentTarget.style.background=''}>
                <div style={{ width:38, height:38, borderRadius:9, flexShrink:0, background:'#FEF3E2',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {isGF
                    ? <SvgIcon size={19} color={ORANGE}><path d="M3 21l18 0M3 10l18 0M5 6l7 -3l7 3M4 10l0 11M20 10l0 11M8 14l0 3M12 14l0 3M16 14l0 3"/></SvgIcon>
                    : <SvgIcon size={19} color={ORANGE}><path d="M11.46 20.846a12 12 0 0 1 -7.96 -14.846a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3a12 12 0 0 1 -.09 7.06M15 19l2 2l4 -4"/></SvgIcon>}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:500, color:'#1A1A1A' }}>{item.client_nom}</div>
                  <div style={{ fontSize:12, color:'#64748B', marginTop:1, fontFamily:'monospace' }}>
                    {item.n_police||item.n_devis} · {item.marche_pays}
                  </div>
                </div>
                <div style={{ textAlign:'right', minWidth:90 }}>
                  <div style={{ fontSize:14, fontWeight:500, color:'#1A1A1A' }}>{fmt(item.prime_ttc)}</div>
                  <div style={{ fontSize:12, color:exp?'#BA7517':'#94A3B8', fontWeight:exp?500:400, display:'flex', alignItems:'center', gap:4, justifyContent:'flex-end' }}>
                    {exp && <SvgIcon size={12} color="#BA7517"><path d="M12 9v4M10.36 3.59l-8.1 13.53a1.9 1.9 0 0 0 1.63 2.88h16.22a1.9 1.9 0 0 0 1.63 -2.87l-8.1 -13.54a1.9 1.9 0 0 0 -3.27 0zM12 16h.01"/></SvgIcon>}
                    {fmtDate(item.date_echeance)}
                  </div>
                </div>
                <span style={{ background:s.bg, color:s.color, padding:'4px 11px', borderRadius:99, fontSize:11, fontWeight:500,
                  display:'inline-flex', alignItems:'center', gap:6 }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:s.dot }}/>
                  {STATUTS[item.statut]||item.statut}
                </span>
                <SvgIcon size={18} color="#CBD5E1"><path d="M9 6l6 6l-6 6"/></SvgIcon>
              </div>
            );
          })}
          {items.length===0 && (
            <div style={{ padding:32, textAlign:'center', color:'#94A3B8', fontSize:14 }}>{t('common.no_results')}</div>
          )}
        </div>
      )}
    </div>
  );
}
